<template>
    <div class="row">
        <snappy-table
                :endpoint="'vessels/'+ vesselId +'/contacts/' + contact.id + '/campaigns'"
                :columns="['status','name','active','launch','created_at']"
                :headings="['Subscribed','Campaign','Status','Launch Date','Created']"
                :template-methods="columnTemplates(timezone)"
                :templated-slot-names="['name', 'status', 'active']"
                table-class="subscribers-table"
                :filter-values="filterValues"
                :filterable-columns="['status', 'name']"
                record-type="Campaigns"
        >
            <template #name="{ row }">
                {{ row.name }} <span class="label label-id">ID: {{ row.id }}</span>
            </template>

            <template #status="{ row }">
                <div class="d-inline-flex">
                    <div v-if="contact.global_unsubscribe" :class="'subscription-label cursor-not-allowed label label-default label-'+ row.id" title="Contact is globally unsubscribed">
                        <div class="place-holder unsubscribed">
                            <i class="icon fas fa-times" aria-hidden="true"></i> UNSUBSCRIBED
                        </div>
                    </div>
                    <div @click="handleSubscribers(row)" v-else-if="row.status === 'subscribed'" :class="'subscription-label hoverable subscribed label label-default label-'+ row.id">
                        <div class="place-holder subscribed">
                            <i class="icon fas fa-check" aria-hidden="true"></i> SUBSCRIBED
                        </div>
                        <div class="action-wrapper unsubscribe-action">
                            <i class="icon fas fa-times" aria-hidden="true"></i> UNSUBSCRIBE
                        </div>
                    </div>
                    <div @click="handleSubscribers(row)" v-else-if="row.status === 'unsubscribed'" :class="'subscription-label hoverable label label-default label-'+ row.id">
                        <div class="place-holder unsubscribed">
                            <i class="icon fas fa-times" aria-hidden="true"></i> UNSUBSCRIBED
                        </div>
                        <div class="action-wrapper subscribe-neutral">
                            <i class="icon fas fa-check" aria-hidden="true"></i> RE-SUBSCRIBE
                        </div>
                    </div>
                    <div @click="handleSubscribers(row)" v-else-if="row.status === 'pending'" :class="'subscription-label hoverable label label-default label-'+ row.id">
                        <div class="place-holder subscribed">
                            <i class="icon fas fa-check" aria-hidden="true"></i> PENDING
                        </div>
                        <div class="action-wrapper unsubscribe-action">
                            <i class="icon fas fa-times" aria-hidden="true"></i> CLEAR
                        </div>
                    </div>
                    <div v-else @click="handleSubscribers(row)" :class="'subscription-label hoverable label label-default label-'+ row.id">
                        <div class="place-holder subscribe-neutral"><i class="icon fas fa-plus" aria-hidden="true"></i></div>
                        <div class="action-wrapper subscribe-neutral">
                            <i class="icon fas fa-check" aria-hidden="true"></i> SUBSCRIBE
                        </div>
                    </div>
                    <button
                        @click="() => openSubscribeModal({ contact, campaign: row })"
                        class="clock-button"
                        :disabled="row.status === 'unsubscribed' || row.status === 'subscribed' || row.global_unsubscribe"
                        v-tooltip="pendingTooltip(row)"
                    ><i
                        class="icon fas fa-clock"
                        aria-hidden="true"
                    ></i></button>
                </div>
            </template>
            <template #active="{ row }">
                <span v-if="row.active === true" class='label label-success'>RUNNING</span>
                <span v-else class='label label-default'>STOPPED</span>
            </template>
        </snappy-table>
        <subscribe-contact-modal
            v-if="campaignToSubscribe"
            :campaign="campaignToSubscribe"
            :contact="contact"
            :timezone="timezone"
            @subscribe="(...args) => handleSubscribe(...args)"
            @close-modal="() => campaignToSubscribe = null"

        />
    </div>
</template>

<style scoped>
.clock-button {
  border: 1px solid #ddd;
  background: transparent;
  color: #ddd;
  padding: .2em .6em .3em;
  height: 30px;
  margin-left: 5px;
  font-size: 15px;
}
.clock-button:hover {
  border: 1px solid #fff;
  background: #399;
  color: #eff;
}
.clock-button:disabled {
  border: 1px solid #ddd;
  background: #eee;
  color: #5f5f5f;
  opacity: 1 !important;
  cursor: not-allowed !important;
  pointer-events: all !important;
}
</style>

<script>
    import SubscribeContactModal from '../../components/contact/SubscribeContactModal';
    import SnappyTable from '../../components/SnappyTable';
    import dateFormatter from "../../mixins/dateFormatter";
    import columnTemplates from "../../mixins/tables/columnTemplates";
    import {subscribeContactToCampaign} from '../../utility/api/campaign/subscribeContactToCampaign';
    import {unsubscribeContactFromCampaign} from '../../utility/api/campaign/unsubscribeContactFromCampaign';
    import {confirmationHelper} from '../../utility/confirmationHelper';
    import {convertServerDateToLocalMoment} from '../../utility/date/date-converter';

    export default {
        name: 'contact-edit-campaign-subscriptions-table',
        mixins: [columnTemplates],
        components: {SnappyTable, SubscribeContactModal},
        data() {
            return {
                filterValues: {
                    status : [
                        {'id': 'subscribed', 'text': 'Subscribed'},
                        {'id': 'unsubscribed', 'text': 'Unsubscribed'},
                        {'id': 'pending', 'text': 'Pending'}
                    ]
                },
                campaignToSubscribe: null,
            };
        },
        methods: {
            handleSubscribers(row) {
                if( row.status === 'subscribed' || row.status === 'pending') {
                    this.handleStatusClick(row, row.status === 'subscribed' ? 'unsubscribed' : 'campaign');
                    unsubscribeContactFromCampaign(row.id, this.contact.id);
                } else if ( row.status === 'unsubscribed' || row.status === 'campaign') {
                    if(row.status === 'unsubscribed') {
                        let text = 'This contact had been unsubscribed from this campaign before. Are you sure you want to re-subscribe this contact?';

                        confirmationHelper.warning(text).then( (isConfirmed) => {

                            if( !isConfirmed ) {
                                return false;
                            }

                            this.handleSubscribe({ contact: this.contact, campaign: row })
                        });

                        return false;
                    }
                    this.handleSubscribe({ contact: this.contact, campaign: row })
                }
            },
            openSubscribeModal(row) {
                this.campaignToSubscribe = row.campaign;
            },
            handleSubscribe(data) {
                this.handleStatusClick(data.campaign, data.subscribeAt ? 'pending' : 'subscribed');
                subscribeContactToCampaign(data.campaign.id, this.contact.id, data.subscribeAt);
                data.campaign.subscribe_at = data.subscribeAt;
            },
            handleStatusClick(row, status) {
                row.status = status;

                setTimeout(() => {
                    let els = document.getElementsByClassName('label-' + row.id);

                    for(let i=0; i < els.length; i++) {

                        if( typeof els[i] !== 'undefined' ) {
                            els[i].classList.remove('hoverable');
                        }
                    }
                },50);

                setTimeout(() => {
                    let els = document.getElementsByClassName('label-' + row.id);

                    for(let i=0; i < els.length; i++) {
                        els[i].addEventListener("mouseout", function handler(e) {
                            if( typeof els[i] !== 'undefined' ) {
                                els[i].classList.add('hoverable');
                            }

                            e.currentTarget.removeEventListener(e.type, handler);
                        });
                    }
                },250);
            },
            pendingTooltip(row) {
                if (row.status === 'pending') {
                    return convertServerDateToLocalMoment(row.subscribe_at, 'YYYY-MM-DD HH:mm:ss', this.timezone).format('MM/DD/YYYY hh:mm A')
                }
            }
        },
        props: {
            vesselId: {
                type: Number,
                required: true
            },
            contact: {
                type: Object,
                required: true
            },
            timezone: {
                type: String,
                required: true
            }
        }
    }
</script>
